import { useEffect, useRef, useState } from "react";
import { BarChart } from "chartist";
import "chartist/dist/index.css";
import "./MyHorizontalBarChart.css";
import MyLegend from "./MyLegend";

const BAR_PALETTE = ["#1976d2", "#2e7d32", "#d32f2f", "#ed6c02", "#8168b3", "#009ec1"];

export default function MyHorizontalBarChart({
  data = { labels: [], series: [] },
  height = 300,
  stackBars = false,
  rotateLabels = false,
  showLegend=true
}) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    text: "",
  });

  useEffect(() => {
    const labels = Array.isArray(data?.labels) ? data.labels : [];
    const seriesList = Array.isArray(data?.series) ? data.series : [];
    const allValues = seriesList.flatMap((seriesItem) => {
      if (Array.isArray(seriesItem)) {
        return seriesItem;
      }
      if (Array.isArray(seriesItem?.data)) {
        return seriesItem.data;
      }
      return [];
    });
    const longestValueLabelLength = allValues.reduce((max, value) => {
      const normalized = Number(value);
      if (!Number.isFinite(normalized)) {
        return max;
      }
      const valueText = Number.isInteger(normalized)
        ? String(normalized)
        : normalized.toFixed(1);
      return Math.max(max, valueText.length);
    }, 0);
    const longestLabelSegmentLength = labels.reduce(
      (max, label) => {
        const segments = String(label ?? "").split(/\s+/).filter(Boolean);
        const longestSegment = segments.reduce(
          (segmentMax, segment) => Math.max(segmentMax, segment.length),
          0,
        );
        return Math.max(max, longestSegment);
      },
      0,
    );
    const dynamicLeftPadding = Math.min(
      90,
      Math.max(28, longestLabelSegmentLength * 7 + 16),
    );
    const dynamicRightPadding = Math.max(35, longestValueLabelLength * 8 + 12);

    const options = {
      height: height - (data.series && data.series.length > 0 ? 10 : 0), // Leave space for legend if series exist
      axisX: {
        onlyInteger: true,
      },
      chartPadding: {
        top: 20,
        right: dynamicRightPadding,
        bottom: rotateLabels === true ? 70 : 10,
        left: dynamicLeftPadding,
      },
      horizontalBars: true,
      stackBars: stackBars,
    };

    chartInstance.current?.detach();

    chartInstance.current = new BarChart(chartRef.current, data, options);
    const handlers = [];
    const isSingleSeries = Array.isArray(data?.series) && data.series.length === 1;

    if (!data.series || data.series.length === 0) {
      return;
    }

    chartInstance.current.on("draw", (ctx) => {
      if (rotateLabels) {
        if (ctx.type === "label" && ctx.axis?.units?.pos === "x") {
          console.log("Rotating label:", ctx.text);
          ctx.element.attr({
            x: ctx.x - 10,
            y: ctx.y + 25,
            transform: `rotate(-45 ${ctx.x - 10} ${ctx.y + 20})`,
            "text-anchor": "end",
          });
        }
      }

      if (ctx.type !== "bar") return;

      if (isSingleSeries) {
        const barColor = BAR_PALETTE[(ctx.index ?? 0) % BAR_PALETTE.length];
        ctx.element.attr({ style: `stroke: ${barColor}` });
      }

      const node = ctx.element?._node;
      if (!node) return;

      const seriesName =
        ctx.series?.name || `Series ${Number(ctx.seriesIndex ?? 0) + 1}`;

      const value =
        ctx.value?.y ??
        ctx.value?.x ??
        (typeof ctx.value === "number" ? ctx.value : "");

      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        const valueText = Number.isInteger(numericValue)
          ? String(numericValue)
          : numericValue.toFixed(1);
        const barStartX = Math.min(ctx.x1, ctx.x2);
        const barEndX = Math.max(ctx.x1, ctx.x2);
        const barLength = barEndX - barStartX;
        const barCenterY = (ctx.y1 + ctx.y2) / 2;
        const shouldRenderInside = barLength >= 40;

        ctx.group.elem(
          "text",
          {
            x: shouldRenderInside ? barEndX - 6 : barEndX + 6,
            y: barCenterY + 4,
            "text-anchor": shouldRenderInside ? "end" : "start",
            style: `fill: ${shouldRenderInside ? "#ffffff" : "#333333"}; font-size: 12px; font-weight: 600;`,
          },
          "ct-value-label",
        ).text(valueText);
      }

      const onMove = (event) => {
        const containerRect = chartRef.current.getBoundingClientRect();

        setTooltip({
          visible: true,
          x: event.clientX - containerRect.left + 10,
          y: event.clientY - containerRect.top - 10,
          text: `${seriesName}: ${value}`,
        });
      };

      const onLeave = () => {
        setTooltip((t) => ({ ...t, visible: false }));
      };

      node.style.cursor = "pointer";
      node.addEventListener("mousemove", onMove);
      node.addEventListener("mouseleave", onLeave);

      handlers.push({ node, onMove, onLeave });
    });

    return () => {
      handlers.forEach(({ node, onMove, onLeave }) => {
        node.removeEventListener("mousemove", onMove);
        node.removeEventListener("mouseleave", onLeave);
      });
      chartInstance.current?.detach();
    };
  }, [data, height, rotateLabels, stackBars]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      <div ref={chartRef} />
      {showLegend && data.series && <MyLegend series={data.series} />}

      {tooltip.visible && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            pointerEvents: "none",
            background: "#222",
            color: "#fff",
            padding: "6px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            whiteSpace: "nowrap",
            zIndex: 10,
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
